/**
 * Concrete Maturity Inputs page (Nurse-Saul datum temperatures and Arrhenius
 * activation energies).
 *
 * Migrated from the legacy ConXEdge MaturityNurseSaulList.aspx and
 * MaturityArrheniusList.aspx (plus their Add/Edit/Delete handlers). Both
 * entities are company-scoped and presented here as sections of the single
 * /maturity-inputs menu, each an expandable-row table (the Products list
 * format) with add/edit/delete. Each group is a "maturity equation", the name
 * the mix design pages use when one is selected; the page never calls them
 * targets (strength targets are a different thing).
 *
 * The page is a grey sidebar (components/maturity/MaturityGuide) with five
 * sections, the ?tab= value (components/maturity/maturitySections):
 * Introduction (the default) and ASTM C1074 (guide pages), then, under ASTM
 * C1074, TTF - Temperature Time Factor (Nurse-Saul) and EA - Equivalent Age
 * (Arrhenius), each the method's explanation with its groups table
 * underneath, then Datum Temperature & Q-Value: the ASTM C1074-19 Appendix X1
 * workbench (DatumQWorkbench) that derives a mix-specific datum temperature
 * and Q-value from mortar cube tests and hands them to the two create
 * dialogs. Old ?guide= links still land on the matching section.
 *
 * Locked groups (the company's TTF ASTM default, or one a finished
 * calibration or started pour uses) show a padlock. They can still be renamed
 * (Group ID and description) but not deleted, and their values are read-only
 * in the edit dialog; "Duplicate as new" copies them for different values.
 * The API refuses value changes and deletes for them (409).
 * The API also refuses to delete a group a mix design still selects (409,
 * naming the mix designs); that reason is shown inside the delete dialog.
 *
 * Note: the backend still serves these under /maturity-targets/*.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
  Typography,
  Card,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  ContentCopy as DuplicateIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import maturityService, {
  type NurseSaulTarget,
  type ArrheniusTarget,
} from '../services/maturity';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import { useCompanyStrengthUnit } from '../utils/useCompanyStrengthUnit';
import { apiErrorText } from '../utils/apiError';
import EmptyState from '../components/EmptyState';
import { HEADER_CELL_SX, DetailItem } from '../components/ListPageShared';
import NurseSaulDialog, { type NurseSaulFormValues } from '../components/maturity/NurseSaulDialog';
import ArrheniusDialog, { type ArrheniusFormValues } from '../components/maturity/ArrheniusDialog';
import DatumQWorkbench from '../components/maturity/DatumQWorkbench';
import { cToF } from '../components/maturity/tempConvert';
import MaturitySidebarLayout, {
  AstmC1074Overview,
  EquivalentAgeInfo,
  MaturityIntroduction,
  TemperatureTimeFactorInfo,
  type GuideNav,
} from '../components/maturity/MaturityGuide';
import { sectionFromSearch, type MaturitySection } from '../components/maturity/maturitySections';

type GroupKind = 'nurse-saul' | 'arrhenius';

const MaturityInputs: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const canManage = hasPermission(user, 'maturity_inputs.create');
  // The lab strengths in the Datum / Q workbench default to the company's
  // strength unit (StrengthFormat).
  const companyStrengthUnit = useCompanyStrengthUnit(user?.CompanyID);
  const activeTab: MaturitySection = sectionFromSearch(searchParams);

  const [nurseSaul, setNurseSaul] = useState<NurseSaulTarget[]>([]);
  const [arrhenius, setArrhenius] = useState<ArrheniusTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialised, setInitialised] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Dialog state
  const [nsDialog, setNsDialog] = useState<{ open: boolean; editing: NurseSaulTarget | null }>({ open: false, editing: null });
  const [arDialog, setArDialog] = useState<{ open: boolean; editing: ArrheniusTarget | null }>({ open: false, editing: null });
  // Pre-filled values handed over from the datum/Q workbench (create only)
  const [nsPrefill, setNsPrefill] = useState<Partial<NurseSaulFormValues> | undefined>(undefined);
  const [arPrefill, setArPrefill] = useState<Partial<ArrheniusFormValues> | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  // Delete state (holds the row and which entity)
  const [deleteTarget, setDeleteTarget] = useState<{ kind: GroupKind; id: string; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Why the server refused the delete (e.g. mix designs still use the
  // equation), shown in the dialog so the codes stay readable.
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    setDeleteError('');
  }, [deleteTarget]);

  // Table state: one expanded row and client-side paging (lists are fetched whole)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setExpandedId(null);
    setPage(0);
    setQuery('');
  }, [activeTab]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ns, ar] = await Promise.all([
        maturityService.listNurseSaul(),
        maturityService.listArrhenius(),
      ]);
      setNurseSaul(Array.isArray(ns) ? ns : []);
      setArrhenius(Array.isArray(ar) ? ar : []);
    } catch (err: any) {
      setError(apiErrorText(err, t('maturityTarget.loadError', 'Failed to load maturity equations')));
    } finally {
      setLoading(false);
      setInitialised(true);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const goToTab = useCallback(
    (value: MaturitySection) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', value);
      next.delete('guide');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Old guide links (?tab=introduction&guide=...): rewrite to the section they now open.
  const hasLegacyGuide = searchParams.has('guide');
  useEffect(() => {
    if (hasLegacyGuide) goToTab(activeTab);
  }, [hasLegacyGuide, activeTab, goToTab]);

  // ---- Save handlers ----
  const handleSaveNurseSaul = async (values: NurseSaulFormValues) => {
    if (!user?.CompanyID) {
      setSnackbar({ msg: t('maturityTarget.missingCompany', 'Missing company context'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (nsDialog.editing) {
        // A locked group keeps its datum: send only its name.
        await maturityService.updateNurseSaul(nsDialog.editing.id, {
          group_id: values.group_id.trim(),
          group_description: values.group_description.trim(),
          ...(nsDialog.editing.is_locked ? {} : { datum_temp: Number(values.datum_temp) }),
        });
        setSnackbar({ msg: t('maturityTarget.saved', 'Saved successfully'), severity: 'success' });
      } else {
        await maturityService.createNurseSaul({
          company_id: user.CompanyID,
          group_id: values.group_id.trim(),
          group_description: values.group_description.trim(),
          datum_temp: Number(values.datum_temp),
        });
        setSnackbar({ msg: t('maturityTarget.created', 'Created successfully'), severity: 'success' });
        // Created from the lab-data workbench: show the new row. The workbench
        // stays mounted, so its data survives the tab change.
        if (nsPrefill !== undefined) goToTab('nurse-saul');
      }
      setNsDialog({ open: false, editing: null });
      setNsPrefill(undefined);
      fetchAll();
    } catch (e: any) {
      setSnackbar({ msg: apiErrorText(e, t('maturityTarget.saveError', 'Save failed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveArrhenius = async (values: ArrheniusFormValues) => {
    if (!user?.CompanyID) {
      setSnackbar({ msg: t('maturityTarget.missingCompany', 'Missing company context'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (arDialog.editing) {
        await maturityService.updateArrhenius(arDialog.editing.id, {
          group_id: values.group_id.trim(),
          group_description: values.group_description.trim(),
          ...(arDialog.editing.is_locked
            ? {}
            : {
                activation_energy: Number(values.activation_energy),
                specified_temp: Number(values.specified_temp),
              }),
        });
        setSnackbar({ msg: t('maturityTarget.saved', 'Saved successfully'), severity: 'success' });
      } else {
        await maturityService.createArrhenius({
          company_id: user.CompanyID,
          group_id: values.group_id.trim(),
          group_description: values.group_description.trim(),
          activation_energy: Number(values.activation_energy),
          specified_temp: Number(values.specified_temp),
        });
        setSnackbar({ msg: t('maturityTarget.created', 'Created successfully'), severity: 'success' });
        if (arPrefill !== undefined) goToTab('arrhenius');
      }
      setArDialog({ open: false, editing: null });
      setArPrefill(undefined);
      fetchAll();
    } catch (e: any) {
      setSnackbar({ msg: apiErrorText(e, t('maturityTarget.saveError', 'Save failed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete ----
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      if (deleteTarget.kind === 'nurse-saul') {
        await maturityService.deleteNurseSaul(deleteTarget.id);
      } else {
        await maturityService.deleteArrhenius(deleteTarget.id);
      }
      setSnackbar({ msg: t('maturityTarget.deleted', 'Deleted successfully'), severity: 'success' });
      setDeleteTarget(null);
      fetchAll();
    } catch (e: any) {
      setDeleteError(apiErrorText(e, t('maturityTarget.deleteError', 'Delete failed')));
    } finally {
      setDeleting(false);
    }
  };

  const fmtTemp = (c: number): string => {
    if (c === null || c === undefined || Number.isNaN(c)) return '';
    return `${(Math.round(c * 10) / 10).toFixed(1)}`;
  };

  // A locked group cannot change: open the create dialog pre-filled with its values.
  const duplicateAsNew = (kind: GroupKind, row: NurseSaulTarget | ArrheniusTarget) => {
    const base = {
      group_id: `${row.group_id} copy`.slice(0, 50),
      group_description: row.group_description ?? '',
    };
    if (kind === 'nurse-saul') {
      setNsPrefill({ ...base, datum_temp: String((row as NurseSaulTarget).datum_temp) });
      setNsDialog({ open: true, editing: null });
    } else {
      const ar = row as ArrheniusTarget;
      setArPrefill({
        ...base,
        activation_energy: String(ar.activation_energy),
        specified_temp: String(ar.specified_temp),
      });
      setArDialog({ open: true, editing: null });
    }
  };

  // ---- Table rendering (Products list format) ----
  type GroupRow = NurseSaulTarget | ArrheniusTarget;
  interface ValueColumn {
    key: string;
    label: string;
    render: (row: any) => React.ReactNode;
  }

  const fmtF = (c: number): string => {
    const f = Number(cToF(String(c)));
    return Number.isNaN(f) ? '' : f.toFixed(1);
  };

  const nurseSaulColumns: ValueColumn[] = [
    { key: 'datum_temp', label: t('maturityTarget.datumTempC', 'Datum Temp (°C)'), render: (r: NurseSaulTarget) => fmtTemp(r.datum_temp) },
    { key: 'datum_temp_f', label: t('maturityTarget.datumTempF', 'Datum Temp (°F)'), render: (r: NurseSaulTarget) => fmtF(r.datum_temp) },
  ];

  const arrheniusColumns: ValueColumn[] = [
    { key: 'activation_energy', label: t('maturityTarget.activationEnergyShort', 'Activation Energy (J/mol)'), render: (r: ArrheniusTarget) => r.activation_energy },
    { key: 'specified_temp', label: t('maturityTarget.specifiedTempC', 'Specified Temp (°C)'), render: (r: ArrheniusTarget) => fmtTemp(r.specified_temp) },
    { key: 'specified_temp_f', label: t('maturityTarget.specifiedTempF', 'Specified Temp (°F)'), render: (r: ArrheniusTarget) => fmtF(r.specified_temp) },
    { key: 'q', label: t('maturityTarget.q', 'Q'), render: (r: ArrheniusTarget) => r.q },
  ];

  const edgeColor = (row: GroupRow) => (row.is_locked ? theme.palette.grey[400] : theme.palette.primary.main);

  // Padlock (reason on hover) for locked groups.
  const lockIcon = (row: GroupRow) =>
    row.is_locked ? (
      <Tooltip title={row.lock_reason || t('maturityTarget.locked', 'Locked')}>
        <LockIcon
          fontSize="small"
          color="action"
          aria-label={t('maturityTarget.locked', 'Locked')}
          data-testid={`locked-${row.id}`}
        />
      </Tooltip>
    ) : null;

  const renderExpandedContent = (kind: GroupKind, row: GroupRow, columns: ValueColumn[]) => (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, rowGap: 2 }}>
        <DetailItem label={t('maturityTarget.groupDescription', 'Group Description')}>{row.group_description || '-'}</DetailItem>
        {columns.map((col) => (
          <DetailItem key={col.key} label={col.label}>{col.render(row) ?? '-'}</DetailItem>
        ))}
        {row.is_locked && (
          <DetailItem label={t('maturityTarget.locked', 'Locked')}>
            {row.lock_reason || t('maturityTarget.locked', 'Locked')}
          </DetailItem>
        )}
      </Box>
      {canManage && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {/* Locked groups can be renamed; their values stay read-only in the dialog. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              if (kind === 'nurse-saul') setNsDialog({ open: true, editing: row as NurseSaulTarget });
              else setArDialog({ open: true, editing: row as ArrheniusTarget });
            }}
            sx={{ textTransform: 'none' }}
          >
            {row.is_locked ? t('maturityTarget.rename', 'Rename') : t('common.edit', 'Edit')}
          </Button>
          {row.is_locked ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DuplicateIcon />}
              onClick={() => duplicateAsNew(kind, row)}
              sx={{ textTransform: 'none' }}
            >
              {t('maturityTarget.duplicateAsNew', 'Duplicate as new')}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteTarget({ kind, id: row.id, label: row.group_id || row.id })}
              sx={{ textTransform: 'none' }}
            >
              {t('common.delete', 'Delete')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  const renderGroupTable = (
    kind: GroupKind,
    rows: GroupRow[],
    columns: ValueColumn[],
    empty: { title: string; description: string },
  ) => {
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => `${r.group_id} ${r.group_description ?? ''}`.toLowerCase().includes(q));
      empty = {
        title: t('maturityTarget.noMatch', 'No maturity equations match your search'),
        description: t('maturityTarget.noMatchDesc', 'Try a different group ID or description.'),
      };
    }
    const pageRows = rows.slice(page * pageSize, page * pageSize + pageSize);
    const colSpan = columns.length + 2;
    const toggle = (row: GroupRow) => setExpandedId(expandedId === row.id ? null : row.id);
    const pagination = (
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage')}
        sx={isMobile ? { '& .MuiTablePagination-selectLabel': { display: 'none' } } : undefined}
      />
    );
    const emptyState = <EmptyState title={empty.title} description={empty.description} />;

    if (isMobile) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            pageRows.map((row) => {
              const expanded = expandedId === row.id;
              return (
                <Card key={row.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${edgeColor(row)}` }}>
                  <Box onClick={() => toggle(row)} sx={{ px: 2, py: 1.5, cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                        {row.group_id}
                      </Typography>
                      {lockIcon(row)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }} noWrap>
                      {row.group_description || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                      {columns.map((col) => (
                        <Typography key={col.key} variant="caption" color="text.secondary">
                          {col.label}: {col.render(row)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                      {renderExpandedContent(kind, row, columns)}
                    </Box>
                  </Collapse>
                </Card>
              );
            })
          )}
          {pagination}
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} elevation={1}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_CELL_SX, width: 40, p: 0.5 }} />
              <TableCell sx={HEADER_CELL_SX}>{t('maturityTarget.group', 'Group')}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key} sx={HEADER_CELL_SX} align="right">{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  {loading ? <Typography color="text.secondary">{t('common.loading')}</Typography> : emptyState}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => {
                const expanded = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover
                      onClick={() => toggle(row)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: expanded ? 'action.selected' : undefined,
                        '& > td:first-of-type': { borderLeft: `4px solid ${edgeColor(row)}` },
                      }}
                    >
                      <TableCell sx={{ p: 0.5 }} align="center">
                        <IconButton size="small" aria-label={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}>
                          {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {row.group_id}
                            </Typography>
                            {lockIcon(row)}
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 420 }}>
                            {row.group_description || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell key={col.key} align="right">
                          <Typography variant="body2">{col.render(row)}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 3, py: 2, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
                            {renderExpandedContent(kind, row, columns)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {/* Footer: total on the left, pagination on the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.total', 'Total')}: {rows.length}
          </Typography>
          {pagination}
        </Box>
      </TableContainer>
    );
  };

  // Full-page spinner only on first load; later refreshes must not unmount the
  // workbench (it holds unsaved lab data).
  if (!initialised) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const guideNav: GuideNav = { go: goToTab, open: (path) => navigate(path) };

  // The method's groups, under its explanation: heading, toolbar (search,
  // refresh, add) and the table.
  const renderGroupsSection = (kind: GroupKind) => {
    const isNs = kind === 'nurse-saul';
    return (
      <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
            {isNs
              ? t('maturityTarget.nurseSaulGroups', 'Nurse-Saul equations')
              : t('maturityTarget.arrheniusGroups', 'Arrhenius equations')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isNs
              ? t('maturityTarget.nurseSaulGroupsHint', 'Datum temperatures you can select on a mix design')
              : t('maturityTarget.arrheniusGroupsHint', 'Activation energies and reference temperatures you can select on a mix design')}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            placeholder={t('maturityTarget.searchPlaceholder', 'Search by group ID or description')}
            inputProps={{ 'aria-label': t('maturityTarget.search', 'Search maturity equations') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title={t('common.refresh')}>
            <span>
              <IconButton onClick={fetchAll} disabled={loading} aria-label={t('common.refresh')}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (isNs) {
                  setNsPrefill(undefined);
                  setNsDialog({ open: true, editing: null });
                } else {
                  setArPrefill(undefined);
                  setArDialog({ open: true, editing: null });
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              {isNs
                ? t('maturityTarget.addNurseSaul', 'Add Nurse-Saul Equation')
                : t('maturityTarget.addArrhenius', 'Add Arrhenius Equation')}
            </Button>
          )}
        </Box>

        {isNs
          ? renderGroupTable('nurse-saul', nurseSaul, nurseSaulColumns, {
              title: t('maturityTarget.noNurseSaul', 'No Nurse-Saul equations found'),
              description: t('maturityTarget.noNurseSaulDesc', 'Create a Nurse-Saul equation to get started.'),
            })
          : renderGroupTable('arrhenius', arrhenius, arrheniusColumns, {
              title: t('maturityTarget.noArrhenius', 'No Arrhenius equations found'),
              description: t('maturityTarget.noArrheniusDesc', 'Create an Arrhenius equation to get started.'),
            })}
      </Box>
    );
  };

  return (
    <Box>
      <MaturitySidebarLayout section={activeTab} onSelect={goToTab}>
        {activeTab === 'introduction' && <MaturityIntroduction nav={guideNav} />}
        {activeTab === 'astm-c1074' && <AstmC1074Overview nav={guideNav} />}
        {activeTab === 'nurse-saul' && (
          <>
            <TemperatureTimeFactorInfo />
            {renderGroupsSection('nurse-saul')}
          </>
        )}
        {activeTab === 'arrhenius' && (
          <>
            <EquivalentAgeInfo />
            {renderGroupsSection('arrhenius')}
          </>
        )}

        {/* Kept mounted (hidden) in the other sections so entered lab data and results survive switching. */}
        <Box sx={{ display: activeTab === 'determine' ? 'block' : 'none' }} data-testid="determine-panel">
          <DatumQWorkbench
            canManage={canManage}
            onUseForNurseSaul={(v) => {
              setNsPrefill(v);
              setNsDialog({ open: true, editing: null });
            }}
            onUseForArrhenius={(v) => {
              setArPrefill(v);
              setArDialog({ open: true, editing: null });
            }}
            strengthUnit={companyStrengthUnit}
          />
        </Box>
      </MaturitySidebarLayout>

      <NurseSaulDialog
        open={nsDialog.open}
        editing={nsDialog.editing}
        initialValues={nsPrefill}
        saving={saving}
        onClose={() => !saving && setNsDialog({ open: false, editing: null })}
        onSubmit={handleSaveNurseSaul}
      />
      <ArrheniusDialog
        open={arDialog.open}
        editing={arDialog.editing}
        initialValues={arPrefill}
        saving={saving}
        onClose={() => !saving && setArDialog({ open: false, editing: null })}
        onSubmit={handleSaveArrhenius}
      />

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('maturityTarget.deleteTitle', 'Delete maturity equation?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('maturityTarget.deleteBody', {
              defaultValue: 'Delete "{{label}}"? This cannot be undone.',
              label: deleteTarget?.label ?? '',
            })}
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="delete-error">
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)} variant="filled">
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaturityInputs;
